import { Button, Input, Space, Table } from 'antd';
import { FC, useEffect, useState } from 'react';
import { CategoryTypeOptions } from '../../dto/Category.dto';
import {
  WatchFaceType,
  WatchFaceTypeOptions,
  WFStatus,
  WFStatusOptions,
} from '../../dto/WatchFace.dto';
import { useApproveWatchFace, useGetWatchFacesWithReports } from '../../services/api/reports';
import Expandable from './Expandable';
import { WatchFacePreview } from '../WatchFacePreview/WatchFacePreview';
import dayjs from 'dayjs';
import { SorterResult } from 'antd/es/table/interface';
import { CheckCircleOutlined, CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { getWatchfacePreviewMedia } from '../../utils/getWatchfacePreviewUrl';
import { useDebounce } from '../../hooks/useDebounce';
interface DataType {
  key: string;
  image: { src: string; alt: string; type: 'video' | 'img' };
  depthUrl?: string;
  name: string;
  type: string;
  categoryType: string;
  isFree: boolean;
  isApproved: boolean;
  category: string;
  status: WFStatus;
  tags: string[];

  installsCount: number;
  likesCount: number;
  reportsCount: number;
  lastReportDate: any;
  reports: any[];

  styles: any;
}

const ReportsTable: FC<any> = () => {
  const { Column } = Table;
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [type, setType] = useState<WatchFaceType[] | undefined>();
  const [categoryType, setCategoryType] = useState<string[] | undefined>();
  const [status, setStatus] = useState<WFStatus[] | undefined>();
  const [sortField, setSortField] = useState<SorterResult<DataType>['field']>('reportsCount');
  const [sortOrder, setSortOrder] = useState<SorterResult<DataType>['order']>('descend');
  const [searchValue, setSearchValue] = useState<string>('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('');

  const { mutateAsync: approveWatchFace } = useApproveWatchFace();

  const debouncedSearch = useDebounce((value: string) => {
    setDebouncedSearchValue(value);
    setPage(1);
  }, 500);

  const { data: watchFaces, isLoading } = useGetWatchFacesWithReports({
    page,
    limit,
    type,
    categoryType,
    status,
    sortOrder: sortOrder ?? undefined,
    sortField: typeof sortField === 'string' ? sortField : undefined,
    searchValue: debouncedSearchValue || undefined,
  });

  useEffect(() => {
    if (!isLoading && watchFaces?.data) {
      setTotal(Number(watchFaces.resultsCount));
      setDataSource(
        watchFaces.data?.map((el) => {
          const previewMedia = getWatchfacePreviewMedia(el);
          const depthUrl =
            el.wfType === WatchFaceType.Complication
              ? el.complicationsInfo?.depthUrl
              : el?.styles?.depthUrl || el?.edgeToEdgeInfo?.depthUrl;
          return {
            key: el.id,
            name: el.name,
            type: el.wfType,
            status: el.status,
            category: el.category.name,
            image: {
              src: previewMedia.src,
              alt: el.name,
              type: previewMedia.type,
            },
            depthUrl,
            tags: el.tags,
            isFree: el.isFree,
            isApproved: el.isApproved,
            installsCount: el.installsCount,
            likesCount: el.likesCount,
            reports: el.reports,
            reportsCount: el.reports.length,
            lastReportDate: dayjs(
              Math.max(
                ...el.reports?.map((el) => new Date(el.createdAt).getTime())
              )
            ).format('DD/MM/YYYY'),
            styles: el.styles,
            categoryType: el.category.type,
          };
        })
      );
    }
  }, [isLoading, watchFaces]);

  return (
    <>
      <div className='mb-3' style={{ maxWidth: 400 }}>
        <Input
          placeholder='Search by UUID, username, email or name'
          value={searchValue}
          onChange={(event) => {
            setSearchValue(event.target.value);
            debouncedSearch(event.target.value);
          }}
          allowClear
          prefix={<SearchOutlined />}
          size='large'
        />
      </div>
      <Table
      dataSource={dataSource}
      loading={isLoading}
      pagination={{
        current: page,
        total: total,
        pageSize: limit,
        position: ['bottomCenter', 'topCenter'],
      }}
      onChange={(pagination, filters, sorter) => {
        setType(filters.type?.length ? (filters.type as WatchFaceType[]) : undefined);
        setCategoryType(
          filters.categoryType?.length ? (filters.categoryType as string[]) : undefined
        );
        setStatus(filters.status?.length ? (filters.status as WFStatus[]) : undefined);
        if (!Array.isArray(sorter)) {
          setSortOrder(sorter.order);
          setSortField(sorter.field);
        }
        pagination.current && setPage(pagination.current);
        pagination.pageSize && setLimit(pagination.pageSize);
      }}
      expandable={{
        expandRowByClick: true,
        expandedRowRender: (record) => <Expandable reports={record.reports} />,
      }}
    >
      <Column
        title='Image'
        dataIndex='image'
        key='image'
        render={(image: { src: string }, record: DataType) => (
          <WatchFacePreview
            previewURL={image.src}
            depthURL={record?.depthUrl}
          />
        )}
      />
      <Column title='Name' dataIndex='name' key='name' />
      <Column
        title='Type'
        dataIndex='type'
        key='type'
        filters={WatchFaceTypeOptions}
      />
      <Column title='Category' dataIndex='category' key='category' />
      <Column
        title='Category Type'
        dataIndex='categoryType'
        key='categoryType'
        filters={CategoryTypeOptions}
      />
      <Column
        title='Status'
        dataIndex='status'
        key='status'
        filters={WFStatusOptions}
      />
      <Column
        title='Reports'
        dataIndex='reportsCount'
        key='reportsCount'
        sorter={true}
        defaultSortOrder='descend'
      />
      <Column
        title='Last'
        dataIndex='lastReportDate'
        key='lastReportDate'
        sorter={true}
      />
      <Column
        title='Approved'
        dataIndex='isApproved'
        key='isApproved'
        align={'center'}
        render={(isFree: boolean) => {
          return (
            <>{isFree ? <CheckCircleOutlined /> : <CloseCircleOutlined />}</>
          );
        }}
      />
      <Column
        title='Action'
        key='action'
        render={(_: any, record: DataType) => (
          <Space size='middle'>
            <Button
              onClick={(event) => approveWatchFace(record.key)}
            >
              Approve{' '}
            </Button>
          </Space>
        )}
      />
      </Table>
    </>
  );
};

export default ReportsTable;
